import {
  Alert,
  AlertIcon,
  Button,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Prisma, AssessmentOutcome } from '@duke-hq/svc-api-db';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { constructURL, getUserFullName } from '@duke-hq/lib';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import type { AssessmentSubmission } from './types';
import { IdCard, Mail, SendToBack, Ellipsis, Trash2 } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AssessmentOutcomeModal } from './AssessmentOutcomeModal';
import { AssessmentOutcomeConfirmationDialog } from './AssessmentOutcomeConfirmationDialog';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { StudentResourceEnrolmentStatusSchema as UnitAssessmentEnrolmentStatus } from '@duke-hq/svc-api-db/schemas/enums/StudentResourceEnrolmentStatus.schema';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';

import { useAuthAdmin } from '../../AuthProviderAdmin';
import { useCheckPermission } from '../../hooks';
import { LoginAsButton } from '../../components/LoginAsButton';
import { iconSize } from '@duke-hq/ui/constants/scalable';

type Props = {
  user: AssessmentSubmission['unitAssessmentEnrolment']['user'];
  assessmentSubmission: RouterOutputs['assessments']['getById'];
  drawerActions?: React.ReactNode;
};

const setUserAvatarInfo = (
  user: Prisma.StudentUserGetPayload<{
    omit: { torgId: true };
    include: { studentData: { omit: { torgId: true } } };
  }>,
) => ({
  first_name: user.first_name,
  last_name: user.last_name,
  picture_url: user.picture_url,
});

function StudentDetails({
  user,
}: {
  user: Prisma.StudentUserGetPayload<{
    omit: { torgId: true };
    include: { studentData: { omit: { torgId: true } } };
  }>;
}) {
  return (
    <>
      {user?.studentData?.universal_student_identifier != null ? (
        <HStack align="center" gap="1">
          <Icon as={IdCard} color="fg.subtle" boxSize={iconSize.lg} />
          <Text>{user.studentData.universal_student_identifier}</Text>
        </HStack>
      ) : null}
      {user.email != null ? (
        <HStack align="center" gap="1">
          <Icon as={Mail} color="fg.subtle" boxSize={iconSize.lg} />
          <Text>{user.email}</Text>
        </HStack>
      ) : null}
    </>
  );
}

export function AssessmentUserHeader({ user, assessmentSubmission, drawerActions }: Props) {
  const canUpdateAssessmentOutcome = useCheckPermission('canUpdateAssessmentOutcome');
  const auth = useAuthAdmin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utils = trpcAdmin.useUtils();
  const resetAssessment = trpcAdmin.assessments.resetAssessment.useMutation({
    onSuccess() {
      setIsConfirmingDestruction(false);
      navigate(
        constructURL(
          '/assessments/',
          Object.fromEntries(searchParams.entries()) as Record<string, string>,
        ),
      );
      toast({
        status: 'success',
        title: t`The student assessment has been reset successfully`,
      });
    },
    onError() {
      toast({
        status: 'error',
        title: t`An error occurred while resetting the assessment`,
      });
    },
    onSettled: async () => {
      await Promise.all([
        utils.assessments.getById.setData(Number(assessmentSubmission.id), undefined),
        utils.assessments.all.invalidate(undefined, { refetchType: 'active' }),
        utils.assessments.snapshot.invalidate(),
      ]);
    },
  });

  const updateAssessmentOutcome = trpcAdmin.assessments.updateAssessmentOutcome.useMutation({
    onSettled: async (_data, error) => {
      if (error) {
        toast({
          status: 'error',
          title: t`Error updating assessment outcome`,
          description: t`Student has already made progress in the revision`,
        });
      } else {
        toast({
          status: 'success',
          title: t`Assessment outcome updated successfully`,
        });

        setIsUpdatingAssessmentOutcome(false);

        await Promise.all([
          utils.assessments.all.invalidate(undefined, { refetchType: 'active' }),
          utils.assessments.snapshot.refetch(),
          utils.assessments.getById.refetch(Number(assessmentSubmission.id)),
          utils.assessments.getWouldCompleteCourse.invalidate(),
        ]);
      }
    },
  });

  const assignAssessment = trpcAdmin.assessments.assignAssessment.useMutation({
    onSettled: async () => {
      await Promise.all([
        utils.assessments.all.invalidate(undefined, { refetchType: 'active' }),
        utils.assessments.snapshot.refetch(),
        utils.assessments.getById.refetch(Number(assessmentSubmission.id)),
      ]);
    },
  });

  const handleSelfAssign = () => {
    if (auth.user?.drn !== undefined && assessmentSubmission.id !== undefined) {
      assignAssessment.mutate({
        assessmentSubmissionId: assessmentSubmission.id,
        assessorDrn: auth.user.drn,
      });
    }
  };

  const [isConfirmingDestruction, setIsConfirmingDestruction] = useState(false);
  const [isUpdatingAssessmentOutcome, setIsUpdatingAssessmentOutcome] = useState(false);
  const [isConfirmingOutcomeUpdate, setIsConfirmingOutcomeUpdate] = useState(false);
  const [pendingOutcomeData, setPendingOutcomeData] = useState<{
    outcome: AssessmentOutcome;
    overrideReason?: string;
  } | null>(null);
  const toast = useToast();

  const isCourseEnrolmentCompleted =
    assessmentSubmission.unitAssessmentEnrolment.courseEnrolment.status === 'COMPLETED';

  const { data: wouldCompleteCourseData } = trpcAdmin.assessments.getWouldCompleteCourse.useQuery(
    {
      unitAssessmentId: assessmentSubmission.unit_assessment_id,
      unitAssessmentEnrolmentId: assessmentSubmission.unit_assessment_enrolment_id,
    },
    { enabled: !isCourseEnrolmentCompleted },
  );
  const willCompleteCourseWhenSatisfactory = wouldCompleteCourseData?.wouldCompleteCourse ?? false;

  const handleAssessmentOutcome = useCallback(
    (data: { outcome: AssessmentOutcome; overrideReason?: string }) => {
      const { outcome, overrideReason } = data;

      updateAssessmentOutcome.mutate({
        assessmentSubmissionId: assessmentSubmission.id,
        outcome: outcome,
        ...(overrideReason != null && overrideReason !== '' ? { overrideReason } : {}),
      });
    },
    [assessmentSubmission.id, updateAssessmentOutcome],
  );

  const handleAssessmentOutcomeWithConfirmation = useCallback(
    (data: { outcome: AssessmentOutcome; overrideReason?: string }) => {
      const currentOutcome = assessmentSubmission.outcome;
      const newOutcome = data.outcome;

      // Don't show confirmation dialog when switching between PENDING and NOT_YET_SATISFACTORY
      const isPendingToNotYetSat =
        currentOutcome === AssessmentOutcome.PENDING &&
        newOutcome === AssessmentOutcome.NOT_YET_SATISFACTORY;
      const isNotYetSatToPending =
        currentOutcome === AssessmentOutcome.NOT_YET_SATISFACTORY &&
        newOutcome === AssessmentOutcome.PENDING;
      // Marking SATISFACTORY for the first time when it neither completes the
      // course nor revokes existing documentation: nothing to confirm.
      const isHarmlessSatTransition =
        newOutcome === AssessmentOutcome.SATISFACTORY &&
        currentOutcome !== AssessmentOutcome.SATISFACTORY &&
        !willCompleteCourseWhenSatisfactory &&
        !isCourseEnrolmentCompleted;
      // Reverting from SATISFACTORY when the course hadn't completed: no
      // documentation was ever issued, so nothing to revoke.
      const isHarmlessSatRevert =
        currentOutcome === AssessmentOutcome.SATISFACTORY &&
        newOutcome !== AssessmentOutcome.SATISFACTORY &&
        !isCourseEnrolmentCompleted;

      if (
        isPendingToNotYetSat ||
        isNotYetSatToPending ||
        isHarmlessSatTransition ||
        isHarmlessSatRevert
      ) {
        // Skip confirmation dialog and update directly
        handleAssessmentOutcome(data);
        setIsUpdatingAssessmentOutcome(false);
      } else {
        // Store the data and show confirmation dialog
        setPendingOutcomeData(data);
        setIsConfirmingOutcomeUpdate(true);
        setIsUpdatingAssessmentOutcome(false);
      }
    },
    [
      assessmentSubmission,
      handleAssessmentOutcome,
      willCompleteCourseWhenSatisfactory,
      isCourseEnrolmentCompleted,
    ],
  );

  const handleConfirmOutcomeUpdate = useCallback(() => {
    if (pendingOutcomeData) {
      handleAssessmentOutcome(pendingOutcomeData);
      setIsConfirmingOutcomeUpdate(false);
      setPendingOutcomeData(null);
    }
  }, [pendingOutcomeData, handleAssessmentOutcome]);

  const isUpdateOutcomeDisabled = useMemo(() => {
    const hasStudentMadeProgressInRevision =
      assessmentSubmission.unitAssessmentEnrolment.status ===
      UnitAssessmentEnrolmentStatus.enum.IN_PROGRESS;

    return hasStudentMadeProgressInRevision;
  }, [assessmentSubmission]);

  const isPending = assessmentSubmission?.outcome === AssessmentOutcome.PENDING;

  const isAssignedToOtherUser = Boolean(
    assessmentSubmission.assessedBy && assessmentSubmission.assessedBy.id !== auth.user?.id,
  );

  return (
    <DrawerHeader borderBottomWidth="1px" borderBottomColor="border.default" p={{ base: 2, md: 4 }}>
      <HStack justify="space-between" alignItems="start">
        <HStack align="start" h="full" alignSelf="stretch">
          <UserAvatar user={setUserAvatarInfo(user)} size="lg" />
          <VStack ml="4" align={'start'} spacing={0}>
            <HStack gap={1} align="center">
              <Text fontWeight="bold">{getUserFullName(setUserAvatarInfo(user))}</Text>
              <LoginAsButton kind="student" studentId={user.id} />
            </HStack>
            <HStack
              gap="4"
              align="center"
              justify="start"
              flex="1"
              fontSize="sm"
              lineHeight="5"
              fontWeight="medium"
              color="fg.muted"
            >
              {user.studentData ? <StudentDetails user={user} /> : null}
            </HStack>
          </VStack>
        </HStack>
        <HStack gap="1" flexShrink={0}>
          {canUpdateAssessmentOutcome === true && assessmentSubmission?.id ? (
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<Ellipsis size={iconSize.lg} />}
                variant="ghost"
                size="sm"
              />
              <MenuList p={0}>
                <MenuItem
                  icon={<Trash2 size={iconSize.md} />}
                  onClick={() => {
                    setIsConfirmingDestruction(true);
                  }}
                >
                  <Trans>Delete assessment</Trans>
                </MenuItem>
                <MenuItem
                  icon={<SendToBack size={iconSize.md} />}
                  onClick={() => {
                    setIsUpdatingAssessmentOutcome(true);
                  }}
                  isDisabled={isCourseEnrolmentCompleted || isUpdateOutcomeDisabled}
                >
                  <Trans>Update outcome</Trans>
                </MenuItem>
              </MenuList>
            </Menu>
          ) : null}
          {drawerActions}
        </HStack>
      </HStack>

      {canUpdateAssessmentOutcome === true && assessmentSubmission?.id ? (
        <>
          <ConfirmationDialog
            isOpen={isConfirmingDestruction}
            onClose={() => {
              setIsConfirmingDestruction(false);
            }}
            title={<Trans>Destroy & reset assessment</Trans>}
            body={
              <Trans>
                This action will destroy any feedback created by the assessors, all answers from the
                student and completely reset this assessment submission. The student will have to
                start the assessment for this Unit again from scratch. Are you sure you want to do
                this?
              </Trans>
            }
            onConfirm={() => {
              resetAssessment.mutate({
                assessmentSubmissionId: assessmentSubmission?.id,
              });
            }}
            confirmLabel={<Trans>Yes, reset the assessment</Trans>}
            variant="destructive"
            isLoading={resetAssessment.isPending}
          />

          <AssessmentOutcomeModal
            isOpen={isUpdatingAssessmentOutcome}
            onClose={() => {
              setIsUpdatingAssessmentOutcome(false);
            }}
            onUpdate={handleAssessmentOutcome}
            assessmentSubmission={assessmentSubmission}
            onSubmitWithConfirmation={handleAssessmentOutcomeWithConfirmation}
          />
        </>
      ) : null}

      {isPending ? (
        isAssignedToOtherUser ? (
          <Alert status="warning" mt={4}>
            <AlertIcon />
            <Flex align="center" justify="space-between" width="100%">
              <Text>
                {t`This assessment is being assessed by ${assessmentSubmission?.assessedBy?.first_name ?? ''} ${assessmentSubmission?.assessedBy?.last_name ?? ''}`}
              </Text>
              <Button size="sm" variant="outline" colorScheme="blue" onClick={handleSelfAssign}>
                {t`Assign to myself`}
              </Button>
            </Flex>
          </Alert>
        ) : assessmentSubmission.assessedBy === null ? (
          <Alert status="info" mt={4}>
            <AlertIcon />
            <Flex align="center" justify="space-between" width="100%">
              <Text>{t`This assessment needs an assessor`}</Text>
              <Button size="sm" variant="outline" colorScheme="blue" onClick={handleSelfAssign}>
                {t`Assign to myself`}
              </Button>
            </Flex>
          </Alert>
        ) : null
      ) : null}

      {canUpdateAssessmentOutcome === true ? (
        <AssessmentOutcomeConfirmationDialog
          isOpen={isConfirmingOutcomeUpdate}
          onClose={() => {
            setIsConfirmingOutcomeUpdate(false);
            setPendingOutcomeData(null);
            setIsUpdatingAssessmentOutcome(false);
          }}
          onConfirm={handleConfirmOutcomeUpdate}
          pendingOutcome={pendingOutcomeData?.outcome ?? null}
          isCourseEnrolmentCompleted={isCourseEnrolmentCompleted}
          willCompleteCourseWhenSatisfactory={willCompleteCourseWhenSatisfactory}
        />
      ) : null}
    </DrawerHeader>
  );
}
